import { Query } from 'src/modules/common/components/Query/Query.tsx';
import {
    CountTotalDocument,
    CountTotalQuery,
    CountTotalQueryVariables,
    LocalesEnum, RegionsEnum,
} from 'src/generated/operations.ts';
import { memo, PropsWithChildren, Suspense, useMemo } from 'react';
import { SelectionRepository } from 'src/projects/SelectionRepository.ts';
import { useGetCountsMap } from 'src/projects/hooks/usGetCountsMap.ts';
import { removeDuplicateFragments } from 'src/utils/de-dupe-fragments.ts';
import { Price } from 'src/common/Price/Price';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { SkeletonPrice } from 'src/projects/components/SkeletonPrice.tsx';

type Props = {
    locale: LocalesEnum;
    region: RegionsEnum;
    priceId: string;
    selectionRepository: SelectionRepository;
}

// Built once, on purpose: useSuspenseQuery keys its cache on the document object itself, so a
// document rebuilt on every render counts as a different query and suspends again — which made
// the total blink back to its placeholder on every unrelated re-render (expanding a selection).
const CountTotalDedupedDocument = removeDuplicateFragments(CountTotalDocument);

export const CountTotalPrice = memo(({
    locale,
    region,
    priceId,
    selectionRepository,
    children,
}: PropsWithChildren<Props>) => {
    const map = useGetCountsMap(selectionRepository);

    const variables = useMemo(() => ({
        variables: {
            locale,
            region,
            priceId,
            labels: map,
        },
    }), [locale, region, priceId, map]);

    // Shown until the totals are in: the counts are known from the selections themselves, only
    // the price has to wait for the query.
    const info = (
        <>
            <div className="vss-project-page__summary-info">
                {children}
            </div>

            <div className="vss-project-page__summary-price">
                <SkeletonPrice/>
            </div>
        </>
    );

    return (
        <Suspense fallback={info}>
            <Query<CountTotalQuery, CountTotalQueryVariables>
                query={CountTotalDedupedDocument}
                variables={variables}
            >
                {(data) => {
                    if (data.data === undefined) {
                        return info;
                    }
                    return (
                        <>
                            <div className="vss-project-page__summary-info">
                                {children}
                                <span className="vss-project-page__summary-sep">•</span>
                                {data.data.countTotal.count ?? 0}
                                {' '}
                                <I18n messageId={MESSAGES.projectPageProductsCount}/>
                            </div>

                            <div className="vss-project-page__summary-price">
                                <Price
                                    price={data.data.countTotal.total ?? 0}
                                    currency={data.data.countTotal.currency ?? ''}
                                    locale={locale}
                                />
                            </div>
                        </>
                    );
                }}
            </Query>
        </Suspense>
    );
});
